/**
 * D775 — the declarative SHAPE of a governed setting's value, so the Admin can offer a form instead of a free JSON box.
 *
 * A form describes what the key's validator (`SettingDefinition.parse` in `services/vello-api/src/setting-definitions.ts`) accepts:
 * the fields, their bounds and the closed lists their codes come from. It never replaces the validator — the server still parses
 * the value at preview, proposal and approval — but a form that drifts from it is caught by the api's conformance test (the key's
 * default fits the form, and the sample the form builds parses).
 *
 * Every word a person reads about a form lives in `@vello/vocabulary` (`settings.field.*`, `settings.unit`, the option namespaces):
 * a form carries codes and paths only. Neutral: no country, currency, locale or unit literal beyond the unit codes below.
 */
const ISO4217 = /^[A-Z]{3}$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const join = (base, part) => (base === '' ? part : `${base}.${part}`);
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** Every way `value` does not fit `field` (empty: it fits). Structural only — the key's validator may still refuse a value that fits. */
export function settingFormIssues(field, value, path = '') {
    const out = [];
    const add = (code, at = path) => { out.push({ path: at, code }); };
    switch (field.kind) {
        case 'boolean':
            if (typeof value !== 'boolean')
                add('NOT_BOOLEAN');
            break;
        case 'integer':
            if (typeof value !== 'number' || !Number.isSafeInteger(value))
                add('NOT_INTEGER');
            else if (value < field.min)
                add('BELOW_MIN');
            else if (value > field.max)
                add('ABOVE_MAX');
            else if (field.allowed !== undefined && !field.allowed.includes(value))
                add('NOT_AN_OPTION');
            break;
        case 'decimal':
            if (typeof value !== 'number' || !Number.isFinite(value))
                add('NOT_NUMBER');
            else if (value < field.min)
                add('BELOW_MIN');
            else if (value > field.max)
                add('ABOVE_MAX');
            else if (Math.abs(Math.round(value / field.step) * field.step - value) > field.step / 1e6)
                add('OFF_STEP');
            break;
        case 'choice':
            if (typeof value !== 'string' || !field.options.includes(value))
                add('NOT_AN_OPTION');
            break;
        case 'choices': {
            if (!Array.isArray(value)) {
                add('NOT_LIST');
                break;
            }
            value.forEach((x, i) => { if (typeof x !== 'string' || !field.options.includes(x))
                add('NOT_AN_OPTION', `${path}[${String(i)}]`); });
            if (new Set(value).size !== value.length)
                add('DUPLICATE');
            if (field.minItems !== undefined && value.length < field.minItems)
                add('TOO_FEW');
            if (field.maxItems !== undefined && value.length > field.maxItems)
                add('TOO_MANY');
            break;
        }
        case 'text':
            if (typeof value !== 'string') {
                add('NOT_TEXT');
                break;
            }
            if (field.minLength !== undefined && value.length < field.minLength)
                add('TOO_SHORT');
            else if (value.length > field.maxLength)
                add('TOO_LONG');
            else if (field.pattern !== undefined && !new RegExp(`^(?:${field.pattern})$`, 'u').test(value))
                add('PATTERN');
            break;
        case 'money': {
            const amount = isRecord(value) ? value.amountMinor : undefined;
            const currency = isRecord(value) ? value.currency : undefined;
            if (typeof amount !== 'number' || !Number.isSafeInteger(amount) || amount < 0 || typeof currency !== 'string' || !ISO4217.test(currency))
                add('NOT_MONEY');
            else if (field.maxMinor !== undefined && amount > field.maxMinor)
                add('ABOVE_MAX');
            break;
        }
        case 'time':
            if (typeof value !== 'string' || !TIME.test(value))
                add('NOT_TIME');
            break;
        case 'date':
            if (typeof value !== 'string' || !DATE.test(value))
                add('NOT_DATE');
            break;
        case 'object': {
            if (!isRecord(value)) {
                add('NOT_OBJECT');
                break;
            }
            const names = new Set(field.fields.map((f) => f.name));
            for (const k of Object.keys(value))
                if (!names.has(k))
                    add('UNKNOWN_FIELD', join(path, k));
            for (const f of field.fields) {
                if (value[f.name] === undefined) {
                    if (f.optional !== true)
                        add('REQUIRED', join(path, f.name));
                    continue;
                }
                if (value[f.name] === null && f.nullable === true)
                    continue;
                out.push(...settingFormIssues(f.field, value[f.name], join(path, f.name)));
            }
            break;
        }
        case 'list': {
            if (!Array.isArray(value)) {
                add('NOT_LIST');
                break;
            }
            if (field.minItems !== undefined && value.length < field.minItems)
                add('TOO_FEW');
            if (field.maxItems !== undefined && value.length > field.maxItems)
                add('TOO_MANY');
            value.forEach((x, i) => { out.push(...settingFormIssues(field.item, x, `${path}[${String(i)}]`)); });
            break;
        }
        case 'map': {
            if (!isRecord(value)) {
                add('NOT_OBJECT');
                break;
            }
            const entries = Object.entries(value);
            if (field.minEntries !== undefined && entries.length < field.minEntries)
                add('TOO_FEW');
            for (const [k, x] of entries) {
                const keyOk = field.keys !== undefined ? field.keys.includes(k) : field.keyPattern === undefined || new RegExp(`^(?:${field.keyPattern})$`, 'u').test(k);
                if (!keyOk)
                    add('BAD_KEY', join(path, k));
                if (x === null && field.nullableValues === true)
                    continue;
                out.push(...settingFormIssues(field.value, x, join(path, k)));
            }
            break;
        }
        case 'json':
            if (value === undefined)
                add('REQUIRED');
            break;
    }
    return out;
}
/**
 * The value a NEW item starts from in the editor (a row added to a list, a field switched on): each boolean `false`, each number
 * its `min`, each choice its first option, each list and map with the fewest items it allows, optional fields left out, text empty
 * and a `json` field `null`. It need not pass the key's validator — the operator fills it in.
 */
export function settingFormSample(field) {
    switch (field.kind) {
        case 'boolean': return false;
        case 'integer': return field.allowed?.[0] ?? field.min;
        case 'decimal': return field.min;
        case 'choice': return field.options[0];
        case 'choices': return field.options.slice(0, Math.max(field.minItems ?? 0, 0));
        case 'text': return '';
        case 'money': return { amountMinor: 0, currency: 'XTS' };
        case 'time': return '00:00';
        case 'date': return '2030-01-01';
        case 'object': {
            const o = {};
            for (const f of field.fields)
                if (f.optional !== true)
                    o[f.name] = settingFormSample(f.field);
            return o;
        }
        case 'list': return Array.from({ length: field.minItems ?? 0 }, () => settingFormSample(field.item));
        case 'map': {
            const o = {};
            const keys = (field.keys ?? []).slice(0, field.minEntries ?? 0);
            for (const k of keys)
                o[k] = settingFormSample(field.value);
            return o;
        }
        case 'json': return null;
    }
}
/**
 * The vocabulary key of a field's label: the setting key and the field's path with list positions removed
 * (`pricing.timeBands` + `bands[3].start` → `pricing.timeBands:bands.start`). The whole value has the path `''` and no label.
 */
export function settingFieldLabelKey(settingKey, path) {
    return `${settingKey}:${path.replace(/\[\d+\]/g, '')}`;
}
/**
 * The path a field's words are filed under: map values are written `*` (the form's own path, as in `settingFormPaths`), while a
 * value's concrete path carries the entry's name (`levers.moto.maxPerTripMinor` → `levers.*.maxPerTripMinor`). List positions go.
 */
export function settingFieldLabelPath(form, path) {
    const parts = path.replace(/\[\d+\]/g, '').split('.').filter((p) => p !== '');
    const out = [];
    let at = form;
    for (const part of parts) {
        while (at !== undefined && at.kind === 'list')
            at = at.item;
        if (at === undefined) {
            out.push(part);
        }
        else if (at.kind === 'object') {
            out.push(part);
            at = at.fields.find((f) => f.name === part)?.field;
        }
        else if (at.kind === 'map') {
            out.push('*');
            at = at.value;
        }
        else {
            out.push(part);
            at = undefined;
        }
    }
    return out.join('.');
}
/** Every field path of a form (list items and map values as one path each), for the coverage test of the field labels. */
export function settingFormPaths(field, path = '') {
    switch (field.kind) {
        case 'object':
            return field.fields.flatMap((f) => [join(path, f.name), ...settingFormPaths(f.field, join(path, f.name))]);
        case 'list':
            return settingFormPaths(field.item, `${path}[0]`);
        case 'map':
            return settingFormPaths(field.value, join(path, '*'));
        default:
            return [];
    }
}
/** The choice fields of a form with their path, for the coverage test of the option labels. */
export function settingFormChoices(field, path = '') {
    switch (field.kind) {
        case 'choice':
        case 'choices':
            return [{ path, field }];
        case 'object':
            return field.fields.flatMap((f) => settingFormChoices(f.field, join(path, f.name)));
        case 'list':
            return settingFormChoices(field.item, `${path}[0]`);
        case 'map':
            return settingFormChoices(field.value, join(path, '*'));
        default:
            return [];
    }
}
/**
 * The vocabulary that words a choice field's codes (or a closed map's keys): its own `labels`, else
 * `settings.option.<key>.<path>` in the catalog's id form — dot-separated snake_case, list positions dropped, a map value `*`
 * read as `each` (`dispatch.searchTimeout` + `rideOrigins` → `settings.option.dispatch.search_timeout.ride_origins`).
 */
export function settingOptionVocabulary(settingKey, path, field) {
    if (field.labels !== undefined)
        return field.labels;
    const snake = (s) => (s === '*' ? 'each' : s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/-/g, '_').toLowerCase());
    const parts = [...settingKey.split('.'), ...path.replace(/\[\d+\]/g, '').split('.').filter((p) => p !== '')].map(snake);
    return `settings.option.${parts.join('.')}`;
}
//# sourceMappingURL=form.js.map